import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";

// ── Intersection Observer hook for scroll animations ──

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );

    el.querySelectorAll("[data-reveal]").forEach((child) => {
      (child as HTMLElement).style.opacity = "0";
      observer.observe(child);
    });

    return () => observer.disconnect();
  }, []);

  return ref;
}

// ── Icons (inline SVG for zero-dependency) ──

const Shield = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const Calendar = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const Users = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const FileText = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
  </svg>
);

const Clipboard = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" ry="1" /><path d="M9 14l2 2 4-4" />
  </svg>
);

const MapPin = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
  </svg>
);

const Smartphone = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" /><line x1="12" y1="18" x2="12.01" y2="18" />
  </svg>
);

const Check = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);

// ── Landing Page ──

export function Landing() {
  const { t } = useTranslation();
  const featuresRef = useScrollReveal();
  const haccpRef = useScrollReveal();
  const pricingRef = useScrollReveal();

  const toggleLang = () => {
    const next = i18n.language === "pl" ? "en" : "pl";
    i18n.changeLanguage(next);
    localStorage.setItem("kverna-lng", next);
  };

  return (
    <div className="bg-navy-950 text-white" style={{ fontFamily: "var(--font-body)" }}>
      {/* ─── Nav ─── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-navy-950/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
              <span className="text-navy-950 font-bold text-sm" style={{ fontFamily: "var(--font-display)" }}>K</span>
            </div>
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              Kverna
            </span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-navy-300">
            <a href="#features" className="hover:text-white transition-colors">{t("landing.navFeatures")}</a>
            <a href="#compliance" className="hover:text-white transition-colors">{t("landing.navCompliance")}</a>
            <a href="#pricing" className="hover:text-white transition-colors">{t("landing.navPricing")}</a>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={toggleLang} className="text-xs text-navy-400 hover:text-white transition-colors px-2 py-1 rounded border border-white/10 hover:border-white/20">
              {i18n.language === "pl" ? "EN" : "PL"}
            </button>
            <Link to="/login" className="text-sm text-navy-300 hover:text-white transition-colors hidden sm:block">
              {t("landing.login")}
            </Link>
            <Link to="/register" className="btn btn-primary btn-sm">
              {t("landing.startTrial")}
            </Link>
          </div>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className="relative min-h-[100vh] flex items-center pt-16 hero-grid noise-bg overflow-hidden">
        {/* Radial glow behind hero */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-amber-500/8 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative z-10 max-w-6xl mx-auto px-6 py-32 grid grid-cols-1 xl:grid-cols-2 gap-16 items-center">
          <div>
            <div className="animate-fade-up">
              <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 text-sm text-navy-300 mb-8">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                {t("landing.badge")}
              </div>
            </div>

            <h1
              className="text-5xl sm:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.05] mb-6 animate-fade-up delay-100"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {t("landing.heroTitle1")}{" "}
              <span className="text-amber-400">{t("landing.heroTitle2")}</span>
            </h1>

            <p className="text-xl text-navy-300 leading-relaxed max-w-xl mb-10 animate-fade-up delay-200">
              {t("landing.heroSubtitle")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 animate-fade-up delay-300">
              <Link to="/register" className="btn btn-primary text-base px-8 py-3">
                {t("landing.startTrial")}
                <ArrowRight />
              </Link>
              <a href="#features" className="btn btn-ghost text-base px-8 py-3 border-white/15 text-white hover:bg-white/5 hover:border-white/25">
                {t("landing.seeHow")}
              </a>
            </div>

            <div className="flex items-center gap-6 mt-12 text-sm text-navy-400 animate-fade-up delay-400">
              <span className="flex items-center gap-2">
                <Check /> {t("landing.noCreditCard")}
              </span>
              <span className="flex items-center gap-2">
                <Check /> {t("landing.freeTrial")}
              </span>
            </div>
          </div>

          {/* Hero visual: Dashboard mockup */}
          <div className="hidden xl:block animate-fade-in delay-500">
            <div className="relative">
              <div className="bg-navy-800/60 border border-white/10 rounded-2xl p-5 backdrop-blur-sm shadow-2xl shadow-navy-950/50">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-rose-500/60" />
                  <div className="w-3 h-3 rounded-full bg-amber-500/60" />
                  <div className="w-3 h-3 rounded-full bg-emerald-500/60" />
                  <span className="text-xs text-navy-400 ml-2">{t("landing.mockupTitle")}</span>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[
                    { label: t("landing.mockupClients"), value: "47" },
                    { label: t("landing.mockupWeek"), value: "12" },
                    { label: t("landing.mockupCompliance"), value: "98%" },
                  ].map((s) => (
                    <div key={s.label} className="bg-navy-900/50 rounded-xl p-3 text-center">
                      <p className="text-lg font-bold text-white" style={{ fontFamily: "var(--font-display)" }}>{s.value}</p>
                      <p className="text-[10px] text-navy-400 mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  {[
                    { loc: t("landing.mockupJob1"), time: "09:00", key: "completed" },
                    { loc: t("landing.mockupJob2"), time: "11:30", key: "in_progress" },
                    { loc: t("landing.mockupJob3"), time: "14:00", key: "scheduled" },
                  ].map((j) => (
                    <div key={j.loc} className="flex items-center justify-between bg-navy-900/30 rounded-lg px-3 py-2.5">
                      <div>
                        <p className="text-xs text-white/90">{j.loc}</p>
                        <p className="text-[10px] text-navy-500">{j.time}</p>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                        j.key === "completed" ? "bg-emerald-500/15 text-emerald-400" :
                        j.key === "in_progress" ? "bg-amber-500/15 text-amber-400" :
                        "bg-sky-500/15 text-sky-400"
                      }`}>
                        {t(`jobs.status.${j.key}`)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute -bottom-8 -left-16 w-56 bg-navy-800/80 border border-white/10 rounded-xl p-3 backdrop-blur-sm shadow-xl" style={{ animation: "float 6s ease-in-out infinite" }}>
                <p className="text-[10px] text-navy-400 mb-1.5">{t("landing.mockupInspection")}</p>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span className="text-xs text-emerald-400">{t("landing.mockupClear")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Features ─── */}
      <section id="features" className="relative bg-navy-50 text-navy-900 py-28" ref={featuresRef}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16" data-reveal>
            <p className="text-sm font-semibold text-amber-600 uppercase tracking-widest mb-3">
              {t("landing.featuresTag")}
            </p>
            <h2 className="text-4xl font-bold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              {t("landing.featuresTitle")}
            </h2>
            <p className="text-navy-500 mt-4 max-w-lg mx-auto">
              {t("landing.featuresSubtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: <Users />, title: t("landing.featureClients"), desc: t("landing.featureClientsDesc"), color: "bg-sky-50 text-sky-600" },
              { icon: <Calendar />, title: t("landing.featureScheduling"), desc: t("landing.featureSchedulingDesc"), color: "bg-amber-50 text-amber-600" },
              { icon: <Shield />, title: t("landing.featureHaccp"), desc: t("landing.featureHaccpDesc"), color: "bg-emerald-50 text-emerald-600" },
              { icon: <MapPin />, title: t("landing.featureStations"), desc: t("landing.featureStationsDesc"), color: "bg-violet-50 text-violet-600" },
              { icon: <Clipboard />, title: t("landing.featureInspections"), desc: t("landing.featureInspectionsDesc"), color: "bg-rose-50 text-rose-600" },
              { icon: <Smartphone />, title: t("landing.featureMobile"), desc: t("landing.featureMobileDesc"), color: "bg-teal-50 text-teal-600" },
            ].map((feature, i) => (
              <div
                key={feature.title}
                data-reveal
                className={`bg-white rounded-2xl border border-navy-100 p-6 card-lift delay-${(i + 1) * 100}`}
              >
                <div className={`w-10 h-10 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-lg mb-2" style={{ fontFamily: "var(--font-display)" }}>
                  {feature.title}
                </h3>
                <p className="text-sm text-navy-500 leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── HACCP Compliance Section ─── */}
      <section id="compliance" className="relative bg-navy-900 text-white py-28 noise-bg overflow-hidden" ref={haccpRef}>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[150px] pointer-events-none" />

        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-semibold text-amber-400 uppercase tracking-widest mb-4" data-reveal>
                {t("landing.complianceTag")}
              </p>
              <h2
                className="text-4xl font-bold tracking-tight mb-6"
                style={{ fontFamily: "var(--font-display)" }}
                data-reveal
              >
                {t("landing.complianceTitle1")}<br />{t("landing.complianceTitle2")}
              </h2>
              <p className="text-navy-300 leading-relaxed mb-8" data-reveal>
                {t("landing.complianceDesc")}
              </p>

              <div className="space-y-4" data-reveal>
                {[
                  t("landing.complianceCheck1"),
                  t("landing.complianceCheck2"),
                  t("landing.complianceCheck3"),
                  t("landing.complianceCheck4"),
                  t("landing.complianceCheck5"),
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0 mt-0.5">
                      <Check />
                    </div>
                    <span className="text-sm text-navy-200">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Compliance report mockup */}
            <div data-reveal>
              <div className="bg-white rounded-2xl p-8 text-navy-900 shadow-2xl shadow-navy-950/30">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-xs text-navy-400 uppercase tracking-wider">{t("landing.mockupReport")}</p>
                    <h3 className="font-bold text-lg" style={{ fontFamily: "var(--font-display)" }}>
                      {t("landing.mockupJob1")}
                    </h3>
                  </div>
                  <div className="bg-emerald-50 text-emerald-600 text-xs font-semibold px-3 py-1 rounded-full">
                    {t("landing.mockupCompliant")}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-3 bg-navy-50 rounded-xl">
                    <p className="text-2xl font-bold text-navy-900" style={{ fontFamily: "var(--font-display)" }}>12</p>
                    <p className="text-[11px] text-navy-400">{t("landing.mockupStations")}</p>
                  </div>
                  <div className="text-center p-3 bg-navy-50 rounded-xl">
                    <p className="text-2xl font-bold text-navy-900" style={{ fontFamily: "var(--font-display)" }}>6</p>
                    <p className="text-[11px] text-navy-400">{t("landing.mockupVisits")}</p>
                  </div>
                  <div className="text-center p-3 bg-emerald-50 rounded-xl">
                    <p className="text-2xl font-bold text-emerald-600" style={{ fontFamily: "var(--font-display)" }}>0</p>
                    <p className="text-[11px] text-navy-400">{t("landing.mockupIssues")}</p>
                  </div>
                </div>

                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-navy-400 text-xs border-b border-navy-100">
                      <th className="pb-2 font-medium">{t("landing.mockupStations")}</th>
                      <th className="pb-2 font-medium">{t("locations.type")}</th>
                      <th className="pb-2 font-medium">{t("landing.mockupStatus")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { code: "S1", type: t("locations.stationTypes.bait_station"), ok: true },
                      { code: "S2", type: t("locations.stationTypes.glue_board"), ok: true },
                      { code: "S3", type: t("locations.stationTypes.pheromone_trap"), ok: true },
                      { code: "S4", type: t("locations.stationTypes.bait_station"), ok: true },
                    ].map((s) => (
                      <tr key={s.code} className="border-b border-navy-50">
                        <td className="py-2.5 font-mono text-xs font-medium">{s.code}</td>
                        <td className="py-2.5 text-navy-500">{s.type}</td>
                        <td className="py-2.5">
                          <span className="inline-flex items-center gap-1.5 text-emerald-600 text-xs">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            {t("landing.mockupClearStatus")}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Pricing ─── */}
      <section id="pricing" className="bg-navy-50 text-navy-900 py-28" ref={pricingRef}>
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16" data-reveal>
            <p className="text-sm font-semibold text-amber-600 uppercase tracking-widest mb-3">
              {t("landing.pricingTag")}
            </p>
            <h2 className="text-4xl font-bold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              {t("landing.pricingTitle")}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: t("landing.planStarter"),
                price: "149",
                desc: t("landing.planStarterDesc"),
                features: [
                  t("landing.planFeature2tech"),
                  t("landing.planFeatureClients"),
                  t("landing.planFeatureScheduling"),
                  t("landing.planFeatureReporting"),
                  t("landing.planFeatureMobile"),
                ],
                popular: false,
              },
              {
                name: t("landing.planPro"),
                price: "299",
                desc: t("landing.planProDesc"),
                features: [
                  t("landing.planFeature5tech"),
                  t("landing.planFeatureAllStarter"),
                  t("landing.planFeatureHaccp"),
                  t("landing.planFeatureStations"),
                  t("landing.planFeatureInspections"),
                  t("landing.planFeatureInvoicing"),
                  t("landing.planFeaturePriority"),
                ],
                popular: true,
              },
              {
                name: t("landing.planFirma"),
                price: "499",
                desc: t("landing.planFirmaDesc"),
                features: [
                  t("landing.planFeatureUnlimitedTech"),
                  t("landing.planFeatureAllPro"),
                  t("landing.planFeatureTeam"),
                  t("landing.planFeatureBranding"),
                  t("landing.planFeatureApi"),
                  t("landing.planFeatureDedicated"),
                ],
                popular: false,
              },
            ].map((plan, i) => (
              <div
                key={plan.name}
                data-reveal
                className={`rounded-2xl p-8 card-lift delay-${(i + 1) * 100} ${
                  plan.popular
                    ? "bg-navy-900 text-white border-2 border-amber-500 relative"
                    : "bg-white border border-navy-100"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-amber-500 text-navy-950 text-xs font-bold px-4 py-1 rounded-full">
                    {t("landing.planMostPopular")}
                  </div>
                )}

                <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: "var(--font-display)" }}>
                  {plan.name}
                </h3>
                <p className={`text-sm mb-6 ${plan.popular ? "text-navy-300" : "text-navy-500"}`}>
                  {plan.desc}
                </p>

                <div className="flex items-baseline gap-1 mb-8">
                  <span className="text-4xl font-bold" style={{ fontFamily: "var(--font-display)" }}>
                    {plan.price}
                  </span>
                  <span className={`text-sm ${plan.popular ? "text-navy-400" : "text-navy-400"}`}>
                    {t("landing.planCurrency")}
                  </span>
                  <span className={`text-xs ml-1 ${plan.popular ? "text-navy-500" : "text-navy-400"}`}>
                    netto
                  </span>
                </div>

                <Link
                  to="/register"
                  className={`btn w-full mb-8 ${plan.popular ? "btn-primary" : "btn-secondary"}`}
                >
                  {t("landing.startTrial")}
                </Link>

                <ul className="space-y-3">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm">
                      <span className={plan.popular ? "text-amber-400" : "text-emerald-500"}>
                        <Check />
                      </span>
                      <span className={plan.popular ? "text-navy-200" : "text-navy-600"}>
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="relative bg-navy-950 py-28 noise-bg overflow-hidden">
        <div className="absolute inset-0 hero-grid" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-amber-500/8 rounded-full blur-[120px] pointer-events-none" />

        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2
            className="text-4xl sm:text-5xl font-bold tracking-tight mb-6"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {t("landing.ctaTitle1")}
            <br />
            <span className="text-amber-400">{t("landing.ctaTitle2")}</span>
          </h2>
          <p className="text-navy-300 text-lg mb-10 max-w-lg mx-auto">
            {t("landing.ctaSubtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="btn btn-primary text-base px-10 py-3.5">
              {t("landing.ctaButton")}
              <ArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="bg-navy-950 border-t border-white/5 py-12">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-amber-500 rounded-md flex items-center justify-center">
              <span className="text-navy-950 font-bold text-xs" style={{ fontFamily: "var(--font-display)" }}>K</span>
            </div>
            <span className="text-sm text-navy-400">
              Kverna — {t("app.tagline")}
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm text-navy-500">
            <a href="#features" className="hover:text-navy-300 transition-colors">{t("landing.navFeatures")}</a>
            <a href="#pricing" className="hover:text-navy-300 transition-colors">{t("landing.navPricing")}</a>
            <Link to="/login" className="hover:text-navy-300 transition-colors">{t("landing.login")}</Link>
            <Link to="/regulamin" className="hover:text-navy-300 transition-colors">{t("landing.terms")}</Link>
            <Link to="/prywatnosc" className="hover:text-navy-300 transition-colors">{t("landing.privacy")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
